import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { NavItemButton } from "./NavItemButton";
import { MenuReordenavel } from "./MenuReordenavel";
import { SidebarRodape } from "./SidebarRodape";
import { useGruposVisiveis } from "./use-grupos-visiveis";
import { itemAtivo } from "./nav-items";
import { useOrdemDoMenu } from "./use-ordem-do-menu";
import { DentalbookLogo } from "@/components/DentalbookLogo";
import { useLayoutStyle } from "@/lib/layout-style";
import { cn } from "@/lib/cn";

type SidebarProps = {
  collapsed: boolean;
  onToggleCollapsed: () => void;
};

/** Menu lateral do desktop. No celular não existe: lá a navegação é a barra de abas e a página Menu. */
export function Sidebar({ collapsed, onToggleCollapsed }: SidebarProps) {
  const { pathname } = useLocation();
  const [layoutStyle] = useLayoutStyle();
  const [reordenando, setReordenando] = useState(false);

  const { grupos, reordenarGrupos, reordenarItens, restaurarPadrao, personalizado } =
    useOrdemDoMenu(useGruposVisiveis());

  // Recolhido, a barra é só ícones: não há o que ler pra saber o que se está movendo.
  useEffect(() => {
    if (collapsed) setReordenando(false);
  }, [collapsed]);

  return (
    <aside
      className={cn(
        "group/sidebar sticky top-0 hidden h-screen flex-col border-r border-border bg-white md:flex dark:bg-card",
        collapsed ? "w-[68px]" : "w-[212px]",
      )}
    >
      {/* Botão toggle na linha de divisão — apenas desktop.
          Expandido: sempre visível. Colapsado: aparece no hover do sidebar. */}
      <button
        type="button"
        onClick={onToggleCollapsed}
        aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
        title={collapsed ? "Expandir menu" : "Recolher menu"}
        className={cn(
          "absolute top-20 z-50 hidden h-7 w-7 items-center justify-center rounded-full md:flex",
          "right-[-14px]",
          "border border-border bg-card text-muted-foreground shadow-md",
          "transition-all duration-200 ease-out",
          "hover:bg-primary hover:text-primary-foreground hover:border-primary hover:scale-110 hover:shadow-lg",
          collapsed
            ? "opacity-0 -translate-x-1 group-hover/sidebar:opacity-100 group-hover/sidebar:translate-x-0 focus-visible:opacity-100 focus-visible:translate-x-0"
            : "opacity-100",
        )}
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
        ) : (
          <ChevronLeft className="h-4 w-4" strokeWidth={2.5} />
        )}
      </button>

      <div
        className={cn(
          "flex h-16 shrink-0 items-center justify-between border-b border-border px-4",
          collapsed && "md:justify-center md:px-0",
        )}
      >
        {/* Desktop colapsado: ícone do logo vira botão de expandir (mesmo tamanho h-8 w-8 do expandido — evita salto) */}
        {collapsed && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label="Expandir menu"
            title="Expandir menu"
            className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-muted md:flex"
          >
            <DentalbookLogo className="h-9 w-9" />
          </button>
        )}

        {/* Expandido: logo com texto linkando pro início */}
        <Link
          to="/inicio"
          className={cn(
            "flex items-center gap-2.5 overflow-hidden",
            collapsed && "md:hidden",
          )}
        >
          <DentalbookLogo className="h-11 w-11" />
          <span className="truncate text-lg font-bold text-foreground">
            DentalBook
          </span>
        </Link>
      </div>


      <nav
        className={cn(
          "flex min-h-0 flex-1 flex-col overflow-y-auto",
          layoutStyle === "classic"
            ? "gap-6 p-3"
            : layoutStyle === "colorful"
              ? "gap-4 px-2 py-2"
              : "gap-3 px-2 py-2",
        )}
      >
        {reordenando ? (
          <MenuReordenavel grupos={grupos} onGrupos={reordenarGrupos} onItens={reordenarItens} />
        ) : (
          grupos.map((group) => (
            <div
              key={group.key}
              className={cn("flex flex-col", layoutStyle === "classic" ? "gap-1" : "gap-0.5")}
            >
              {group.label && !collapsed && (
                <p
                  className={cn(
                    "font-semibold uppercase tracking-widest text-muted-foreground",
                    layoutStyle === "classic"
                      ? "px-3 pb-1 text-[10px]"
                      : "px-2.5 pb-0.5 pt-1 text-[9.5px]",
                  )}
                >
                  {group.label}
                </p>
              )}
              {group.items.map((item) => (
                <NavItemButton
                  key={item.href}
                  item={item}
                  active={itemAtivo(item, pathname)}
                  collapsed={collapsed}
                  style={layoutStyle}
                />
              ))}
            </div>
          ))
        )}
      </nav>

      <SidebarRodape
        reordenando={reordenando}
        personalizado={personalizado}
        collapsed={collapsed}
        onEntrar={() => setReordenando(true)}
        onConcluir={() => setReordenando(false)}
        onRestaurar={restaurarPadrao}
      />
    </aside>
  );
}
